import React from 'react';
import { colors } from '../styles/colors';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  loading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  showCancel = true,
  loading = false,
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (onCancel && showCancel && !loading) {
      onCancel();
    }
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>{title}</h2>
        </div>
        <div style={styles.body}>
          <p style={styles.message}>{message}</p>
          {loading && (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <span style={styles.loadingText}>Processando...</span>
            </div>
          )}
        </div>
        <div style={styles.footer}>
          {showCancel && onCancel && (
            <button 
              onClick={onCancel} 
              style={{
                ...styles.cancelBtn,
                ...(loading ? styles.disabledBtn : {})
              }}
              disabled={loading}
            >
              {cancelText}
            </button>
          )}
          <button 
            onClick={onConfirm} 
            style={{
              ...styles.confirmBtn,
              ...(loading ? styles.disabledBtn : {})
            }}
            disabled={loading}
          >
            {loading ? 'Processando...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    minWidth: '400px',
    maxWidth: '500px',
  },
  header: {
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    color: colors.text,
  },
  body: {
    padding: '1.5rem',
  },
  message: {
    margin: 0,
    fontSize: '1rem',
    color: colors.textLight,
    lineHeight: '1.5',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: `2px solid ${colors.border}`,
    borderTop: `2px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '0.9rem',
    color: colors.textLight,
  },
  footer: {
    padding: '1rem 1.5rem',
    borderTop: `1px solid ${colors.border}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'background-color 0.2s',
  },
  confirmBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.danger || '#dc3545',
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'background-color 0.2s',
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default ConfirmModal;
